<template>
  <div class="pt30 mr70 ml70">
    <section class="card">
      <page-title
          :title="$t('powerpointTemplate.title')"
          :path="''"
          @open="showNameHelp = !showNameHelp"
      />
      <form v-if="serverErrorMessage" action="">
        <ul  class="error-msgs">
          <li class="text-danger" :key="`url`">{{serverErrorMessage}}</li>
        </ul>
      </form>
      
      <loader v-if="!formReady && !serverErrorMessage && !processing" :wrapperStyle="{ height: '100px' }" />
      
      <!-- Processing Status Display -->
      <div v-if="processing" class="status-container">
        <h3>{{$t('powerpointTemplate.form.processing_status')}}</h3>
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: processingProgress + '%' }"></div>
        </div>
        <p class="status-message">{{ processingMessage }}</p>
        <div class="progress-percent">{{ processingProgress }}%</div>
      </div>
      
      <div v-if="formReady || processingComplete" class="form-container">
        <label for="multiselect-projects" class="text-gray">
          {{$t('powerpointTemplate.form.selectOnePptx')}}
        </label>
        <form-renderer
        formWrapperClass = "null"
        :model="model"
        :fields="['file_pptx']"
        :showMessageNotifications="false"
        :validateFormTrigger="false"
        :show-submit="false"
        @updateModel="(value, field) => updateModel(value, field)"
        />
        <label for="multiselect-projects" class="text-gray">
          {{$t('powerpointTemplate.form.selectOneXlsx')}}
        </label>
        <form-renderer
        formWrapperClass = 'null'
        :model="model"
        :fields="['file_xlsx']"
        :showMessageNotifications="false"
        :validateFormTrigger="false"
        :show-submit="false"
        @updateModel="(value, field) => updateModel(value, field)"
        />
        <div class="mt10">
          <div class="flex items-center mb10">
            <input type="checkbox" id="format_million_disabledToggle" v-model="model.format_million_disabled" />
            <label for="format_million_disabledToggle" class="ml10">{{$t('powerpointTemplate.form.formatMillionDisabled')}}</label>
          </div>
          <div class="flex items-center mb10">
            <input type="checkbox" id="macro_for_all_charts" v-model="model.macro_for_all_charts" />
            <label for="macro_for_all_charts" class="ml10">{{$t('powerpointTemplate.form.macro_for_all_charts')}}</label>
          </div>
          
          <!-- Show custom VBA code when processing completed and VBA is available -->
          <div v-if="processingComplete && customVbaCode" class="vba-section">
            {{$t('powerpointTemplate.form.manual_vba_required')}}
            <p class="vba-instructions">{{$t('powerpointTemplate.form.manual_vba_required_part2')}}</p>
            <div class="vba-code-container" style="max-height: 300px; overflow-y: auto;">
              <pre @click="selectVbaCode"><code>{{ customVbaCode }}</code></pre>
            </div>
            <p class="vba-help">{{$t('powerpointTemplate.form.manual_vba_required_part3')}}</p>
          </div>
          
          <!-- Show success message when processing is complete -->
          <div v-if="processingComplete && filename" class="mt15">
            <p class="download-info">{{$t('powerpointTemplate.form.success_download')}}</p>
          </div>
        </div>
        <div class="mt10 flex justify-end">
          <button @click="onSubmit" :disabled="waitingPdf || processing" class="button button-save">{{$t('powerpointTemplate.form.send')}}</button>
        </div>
      </div>
    </section>
    <info-modal v-if="showNameHelp" @close="showNameHelp = false" />
    <loader v-if="waitingPdf && !processing" :wrapperStyle="{ height: '100px' }" :class="{  }" />
  </div> 
</template>

<script>
import InfoModal from "../components/powerpoint_template/Modals/InfoModal";
import PageTitle from '../components/outlets/Partials/PageTitle';
import Loader from '../components/loader';
import formRenderer from '../components/form-renderer.vue'
import axios from 'axios';

export default {
  name: 'PowerpointTemplate',
  components: {
    InfoModal,
    Loader,
    formRenderer,
    PageTitle,
  },
  data() {
    return {
      serverErrorMessage: '',
      showNameHelp: false,
      waitingPdf: false,
      processing: false,
      processingProgress: 0,
      processingMessage: '',
      processingComplete: false,
      formReady: true, // Form is ready immediately since we don't need to load VBA
      currentJobId: null,
      pollingInterval: null,
      customVbaCode: null,
      filename: null,  // Store just the filename
      model: {
        ppt_file: null,
        excel_file: null,
        macro_for_all_charts: false,
        format_million_disabled: false,
      },
    }
  },
  beforeDestroy() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
  },
  methods: {
    updateModel(newValue, field) {
        if (!field) return;
        this.model[field.name] = newValue[field.name];
    },
    selectVbaCode() {
      const range = document.createRange();
      const codeElement = document.querySelector('.vba-code-container pre code');
      if (codeElement) {
        range.selectNodeContents(codeElement);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
      }
    },
    onSubmit() {
      const formData = new FormData();
      formData.append('ppt_file', this.model.file_pptx.get('file'));
      formData.append('excel_file', this.model.file_xlsx.get('file'));
      formData.append('format_million_disabled', this.model.format_million_disabled);
      formData.append('macro_for_all_charts', this.model.macro_for_all_charts);
      
      // Reset state
      this.serverErrorMessage = '';
      this.processingComplete = false;
      this.customVbaCode = null;
      this.filename = null;
      this.waitingPdf = true;

      axios.post('/powerpoint_template/upload', formData, {
        timeout: 1800000, // Set timeout to 30 minutes
        responseType: 'json'
      })
      .then(response => {
          this.waitingPdf = false;
          this.processing = true;
          this.currentJobId = response.data.jobId;
          this.processingMessage = response.data.message;
          this.processingProgress = 0;
          
          // Start polling for status
          this.startPolling();
      })
      .catch(error => {
          this.waitingPdf = false;
          this.processing = false;
          this.serverErrorMessage = 'Failed to process files: ' + (error?.response?.data?.error || error.message);
      });
    },
    
    startPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
      }
      
      this.pollingInterval = setInterval(() => {
        this.checkStatus();
      }, 5000);
      
      // Also check immediately
      this.checkStatus();
    },
    
    checkStatus() {
      if (!this.currentJobId) return;
      
      axios.get(`/powerpoint_template/status/${this.currentJobId}`)
        .then(response => {
          const data = response.data;
          
          this.processingProgress = data.progress;
          this.processingMessage = data.message;
          
          if (data.status === 'completed') {
            this.handleCompletion(data);
          } else if (data.status === 'error') {
            this.handleError(data);
          }
          // Continue polling for pending/processing status
        })
        .catch(error => {
          console.error('Status polling error:', error);
          // Don't stop polling on network errors, might be temporary
        });
    },
    
    handleCompletion(data) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      this.processing = false;
      this.processingComplete = true;
      
      // Store custom VBA code if available
      if (data.vbaCode) {
        this.customVbaCode = data.vbaCode;
      }
      
      // Store filename for download
      this.filename = data.filename;
      
      // Show warning if any
      if (data.warning) {
        this.serverErrorMessage = data.warning;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      
      // Auto-download the processed file
      this.downloadProcessedFile();
    },
    
    handleError(data) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      this.processing = false;
      this.processingComplete = false;
      this.serverErrorMessage = 'Processing failed: ' + data.error;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    
    downloadProcessedFile() {
      if (!this.filename) return;
      
      // Construct the proper proxy download URL
      const backendUrl = axios.defaults.baseURL || '';
      const downloadUrl = `/powerpoint_template/download?filename=${encodeURIComponent(this.filename)}`;
      
      const downloadLink = document.createElement('a');
      downloadLink.href = `${backendUrl}${downloadUrl}`;
      downloadLink.download = '';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  }
}
</script>

<style scoped>
.status-container {
  margin: 20px 0;
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: #f9f9f9;
}

.progress-bar {
  width: 100%;
  height: 20px;
  background-color: #f0f0f0;
  border-radius: 10px;
  margin: 10px 0;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #4CAF50;
  border-radius: 10px;
  transition: width 0.3s ease;
}

.status-message {
  margin: 10px 0 5px 0;
  font-weight: 500;
}

.progress-percent {
  font-size: 14px;
  color: #666;
}

.vba-section {
  margin: 20px 0;
  padding: 15px;
  border: 1px solid #ffc107;
  border-radius: 5px;
  background-color: #fff3cd;
}

.vba-instructions {
  color: #856404;
  margin-bottom: 10px;
  font-weight: 500;
}

.vba-help {
  color: #856404;
  font-size: 12px;
  margin-top: 10px;
  font-style: italic;
}

.vba-code-container {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 5px;
  margin: 15px 0;
  border: 1px solid #dee2e6;
}

.vba-code-container pre {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
}

.vba-code-container pre:hover {
  background-color: #e9ecef;
}

.button-download {
  background-color: #28a745;
  color: white;
  margin-right: 10px;
}

.button-download:hover {
  background-color: #218838;
}
</style>